import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Paper, Grid, Typography, MenuItem, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Chip, TablePagination, Link as MuiLink } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import IndustrySummaryCard from '../components/Dashboard/IndustrySummaryCard';
import IndustryCirclePackingGrid from '../components/Dashboard/IndustryCirclePackingGrid';
import TableStylePagination from '../components/Dashboard/TableStylePagination';
import { useCountry } from '../context/CountryContext';
import { API_BASE_URL } from '../config';
import { getCompanySearchLink } from '../utils/companySearch';
import { fetchJsonWithCache, getCachedJson } from '../utils/apiCache';
import { toIndustrySlug } from '../utils/industryRouting';
import { getGradeChipSx } from '../utils/gradeColors';
import { formatCompanySizeLabel } from '../utils/companySize';
import { formatTablePaginationLabel } from '../utils/pagination';
import { mapIndustrySummaryRecords } from '../utils/industryApi';
import { IndustryCompany, IndustryPanel, IndustrySummary } from '../types/industry';

const INDUSTRIES_PER_CIRCLE_PACK_PAGE = 6;
const MIN_COMPANIES_FOR_CIRCLE_PACK = 140;
const INDUSTRY_COMPANIES_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const ByIndustry: React.FC = () => {
  const { country } = useCountry();
  const { industrySlug } = useParams<{ industrySlug?: string }>();
  const [industries, setIndustries] = useState<IndustrySummary[]>([]);
  const [selectedIndustryCode, setSelectedIndustryCode] = useState<number | null>(null);
  const [selectedIndustryData, setSelectedIndustryData] = useState<IndustryPanel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [topIndustryPanels, setTopIndustryPanels] = useState<IndustryPanel[]>([]);
  const [topIndustryPanelsLoading, setTopIndustryPanelsLoading] = useState<boolean>(false);
  const [circlePackingPage, setCirclePackingPage] = useState<number>(1);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [shouldScrollToStats, setShouldScrollToStats] = useState<boolean>(false);
  const industryStatsRef = useRef<HTMLDivElement | null>(null);
  const circlePackingIndustries = useMemo(
    () =>
      [...industries]
        .filter((industry) => industry.name !== 'Unknown' && (industry.count || 0) >= MIN_COMPANIES_FOR_CIRCLE_PACK)
        .sort((a, b) => (b.count || 0) - (a.count || 0)),
    [industries]
  );
  const circlePackingTotalPages = Math.ceil(
    circlePackingIndustries.length / INDUSTRIES_PER_CIRCLE_PACK_PAGE
  );

  useEffect(() => {
    const cacheKey = `industries-summary:${country}`;
    const url = `${API_BASE_URL}/industries/summary?country=${country}`;
    const cachedData = getCachedJson(cacheKey);
    if (cachedData) {
      const mappedIndustries = mapIndustrySummaryRecords(cachedData);

      setIndustries(mappedIndustries);
      const selectedFromSlug = industrySlug
        ? mappedIndustries.find((ind) => toIndustrySlug(ind.codeForSlug) === toIndustrySlug(industrySlug))
        : null;
      if (selectedFromSlug) {
        setSelectedIndustryCode(selectedFromSlug.industry_code);
      } else if (mappedIndustries.length > 0) {
        setSelectedIndustryCode(mappedIndustries[0].industry_code);
      } else {
        setSelectedIndustryCode(null);
        setSelectedIndustryData(null);
      }
      setLoading(false);
      setError(null);
      return;
    }

    const fetchIndustries = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchJsonWithCache(cacheKey, url);
        const mappedIndustries = mapIndustrySummaryRecords(data);

        setIndustries(mappedIndustries);
        const selectedFromSlug = industrySlug
          ? mappedIndustries.find((ind) => toIndustrySlug(ind.codeForSlug) === toIndustrySlug(industrySlug))
          : null;
        if (selectedFromSlug) {
          setSelectedIndustryCode(selectedFromSlug.industry_code);
        } else if (mappedIndustries.length > 0) {
          setSelectedIndustryCode(mappedIndustries[0].industry_code);
        } else {
          setSelectedIndustryCode(null);
          setSelectedIndustryData(null);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to fetch industries');
      } finally {
        setLoading(false);
      }
    };
    fetchIndustries();
  }, [country, industrySlug]);

  useEffect(() => {
    if (selectedIndustryCode === null) return;

    const fetchIndustryDetails = async () => {
      try {
        const detailsCacheKey = `industry-companies:v3:${country}:${selectedIndustryCode}`;
        const detailsUrl = `${API_BASE_URL}/industries/${selectedIndustryCode}/companies?country=${country}`;
        const companies = await fetchJsonWithCache(detailsCacheKey, detailsUrl, INDUSTRY_COMPANIES_TTL_MS);

        const industryInfo = industries.find((industry) => industry.industry_code === selectedIndustryCode);
        if (!industryInfo) {
          setSelectedIndustryData(null);
          return;
        }

        setSelectedIndustryData({
          ...industryInfo,
          companies: Array.isArray(companies) ? (companies as IndustryCompany[]) : [],
        });
        // Reset to first page when industry changes
        setPage(0);
      } catch (err: unknown) {
        console.error('Error fetching industry details:', err);
      }
    };

    fetchIndustryDetails();
  }, [selectedIndustryCode, industries, country]);

  useEffect(() => {
    setCirclePackingPage(1);
  }, [country]);

  useEffect(() => {
    if (circlePackingTotalPages === 0) {
      setCirclePackingPage(1);
      return;
    }
    if (circlePackingPage > circlePackingTotalPages) {
      setCirclePackingPage(circlePackingTotalPages);
    }
  }, [circlePackingTotalPages, circlePackingPage]);

  useEffect(() => {
    const topIndustries = circlePackingIndustries
      .slice(
        (circlePackingPage - 1) * INDUSTRIES_PER_CIRCLE_PACK_PAGE,
        circlePackingPage * INDUSTRIES_PER_CIRCLE_PACK_PAGE
      );

    if (topIndustries.length === 0) {
      setTopIndustryPanels([]);
      setTopIndustryPanelsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTopIndustryPanels = async () => {
      const cacheEntries = topIndustries.map((industry) => {
        const cacheKey = `industry-companies:v3:${country}:${industry.industry_code}`;
        const cached = getCachedJson(cacheKey);
        return { industry, cacheKey, cached };
      });

      const cachedPanels: IndustryPanel[] = cacheEntries
        .filter((entry) => Array.isArray(entry.cached))
        .map((entry) => ({
          ...entry.industry,
          companies: entry.cached as IndustryCompany[],
        }));

      if (!cancelled && cachedPanels.length > 0) {
        setTopIndustryPanels(cachedPanels);
      }

      const missingEntries = cacheEntries.filter((entry) => !Array.isArray(entry.cached));
      setTopIndustryPanelsLoading(missingEntries.length > 0);

      if (missingEntries.length === 0) {
        return;
      }

      try {
        const fetchedPanels = await Promise.all(
          missingEntries.map(async ({ industry, cacheKey }) => {
            const detailsUrl = `${API_BASE_URL}/industries/${industry.industry_code}/companies?country=${country}`;
            const companies = await fetchJsonWithCache(cacheKey, detailsUrl, INDUSTRY_COMPANIES_TTL_MS);
            return {
              ...industry,
              companies: Array.isArray(companies) ? (companies as IndustryCompany[]) : [],
            };
          })
        );

        if (!cancelled) {
          const fetchedByIndustryCode = new Map(
            fetchedPanels.map((panel) => [panel.industry_code, panel])
          );
          const cachedByIndustryCode = new Map(
            cachedPanels.map((panel) => [panel.industry_code, panel])
          );

          const mergedPanels: IndustryPanel[] = topIndustries.map((industry) => (
            fetchedByIndustryCode.get(industry.industry_code) ||
            cachedByIndustryCode.get(industry.industry_code) || {
              ...industry,
              companies: [],
            }
          ));

          setTopIndustryPanels(mergedPanels);
        }
      } catch (err) {
        console.error('Error fetching circle packing panel data:', err);
        if (!cancelled) {
          if (cachedPanels.length === 0) {
            setTopIndustryPanels([]);
          }
        }
      } finally {
        if (!cancelled) {
          setTopIndustryPanelsLoading(false);
        }
      }
    };

    fetchTopIndustryPanels();

    return () => {
      cancelled = true;
    };
  }, [circlePackingIndustries, country, circlePackingPage]);

  const handleCirclePackingIndustrySelect = (industryCode: number) => {
    setShouldScrollToStats(true);
    setSelectedIndustryCode(industryCode);
  };

  useEffect(() => {
    if (!shouldScrollToStats) return;
    if (!industryStatsRef.current) return;
    industryStatsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShouldScrollToStats(false);
  }, [shouldScrollToStats, selectedIndustryCode]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" align="center" sx={{ mt: 4 }}>{error}</Typography>;

  const selectedIndustryName = industries.find(i => i.industry_code === selectedIndustryCode)?.name || '';
  const filteredCompanies: IndustryCompany[] = selectedIndustryData?.companies || [];

  const industrySummaryData = selectedIndustryData ? [
    {
      title: 'Companies in Sector',
      value: selectedIndustryData.count.toLocaleString(),
      bgColor: '#e3f2fd',
      tooltip: 'Total number of companies in this industry sector.'
    },
    {
      title: 'Average Score',
      value: selectedIndustryData.avgScore.toFixed(1),
      bgColor: '#e8f5e8',
      tooltip: 'Average composite pay equity score for companies in this sector.'
    },
    {
      title: 'Average Trend',
      value: selectedIndustryData.avgTrend.toFixed(1),
      bgColor: '#fff3e0',
      tooltip: 'Average year-over-year change in pay equity scores (Trend Score).'
    },
  ] : [];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const unknownIndustries = industries.filter(i => i.name === 'Unknown').length;
  const hasIndustryData = industries.length > 0 && unknownIndustries < industries.length / 2;
  const circlePackingStartIndex = circlePackingIndustries.length === 0
    ? 0
    : (circlePackingPage - 1) * INDUSTRIES_PER_CIRCLE_PACK_PAGE + 1;
  const circlePackingEndIndex = Math.min(
    circlePackingPage * INDUSTRIES_PER_CIRCLE_PACK_PAGE,
    circlePackingIndustries.length
  );

  // Paginate companies
  const paginatedCompanies = filteredCompanies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {hasIndustryData ? (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              Industry Circle Packing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing industries {circlePackingStartIndex}-{circlePackingEndIndex} of {circlePackingIndustries.length} (more than {MIN_COMPANIES_FOR_CIRCLE_PACK} companies), with one packed bubble per company.
            </Typography>
            <IndustryCirclePackingGrid
              panels={topIndustryPanels}
              loading={topIndustryPanelsLoading}
              onIndustrySelect={handleCirclePackingIndustrySelect}
            />
            {circlePackingTotalPages > 1 && (
              <TableStylePagination
                count={circlePackingIndustries.length}
                page={circlePackingPage}
                rowsPerPage={INDUSTRIES_PER_CIRCLE_PACK_PAGE}
                onPageChange={setCirclePackingPage}
                sx={{ mt: 2 }}
              />
            )}
          </Paper>
        ) : (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h6">Industry Data Not Available</Typography>
            <Typography sx={{ mt: 2 }}>
              Detailed industry breakdowns are not yet available for this country.
            </Typography>
          </Paper>
        )}
      </Grid>
      {hasIndustryData && (
        <>
          <Grid item xs={12}>
            <Paper ref={industryStatsRef} elevation={3} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Industry Filter</Typography>
              <TextField
                select
                fullWidth
                label="Select Industry..."
                value={selectedIndustryCode || ''}
                onChange={(e) => setSelectedIndustryCode(Number(e.target.value))}
              >
                {industries.map((opt) => (
                  <MenuItem key={opt.industry_code} value={opt.industry_code}>
                    {opt.name} ({opt.count} companies)
                  </MenuItem>
                ))}
              </TextField>
            </Paper>
          </Grid>

          {selectedIndustryData &&
            <Grid item xs={12}>
              <Grid container spacing={3}>
                {industrySummaryData.map((item, index) => (
                  <Grid item xs={12} sm={4} key={index}>
                    <IndustrySummaryCard {...item} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          }

          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Companies in {selectedIndustryName}</Typography>
              <TableContainer>
                <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Industry Rank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Grade</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>Company Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Company Size</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCompanies.map((row, index: number) => (
                      <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Chip label={row.LetterGrade} size="small" sx={getGradeChipSx(row.LetterGrade)} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <MuiLink
                              component={RouterLink}
                              to={getCompanySearchLink(row.CleanCompanyName || '', row.id ?? undefined)}
                              underline="hover"
                              color="inherit"
                              title={row.CleanCompanyName || undefined}
                              sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {row.CleanCompanyName || 'Unknown company'}
                            </MuiLink>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCompanySizeLabel(row.EmployerSize)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredCompanies.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={formatTablePaginationLabel}
              />
            </Paper>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ByIndustry;
